'use client'

import { cn } from '@/lib/utils'
import { useStoreI18n } from '@/store'
import { Typo3Openings } from '@/types'
import { Accordion, StatusChip } from '@/ui/components'
import React, { memo, useEffect, useState } from 'react'
import { getOpeningTimes, handleClosedStatus } from './utils'

// TODO: tour photo-big Check next opening day

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data?: Typo3Openings
  isDropDown?: boolean
  isBadgeDropdown?: boolean
  itemName?: string
  framed?: boolean
  size?: 's' | 'm'
}

const OpeningStatus: React.FC<Props> = ({
  data,
  className,
  framed = true,
  isDropDown,
  itemName,
  size = 's',
  isBadgeDropdown = false,
  ...props
}) => {
  //States
  const [badgeLabel, setBadgeLabel] = useState<string>()
  const [weekOpenings, setWeekOpenings] = useState<any>('')
  const [isOpenToday, setIsOpenToday] = useState<
    'open' | 'closed' | 'onRequest' | undefined
  >()
  const [calculateOpening, setCalculateOpening] = useState<boolean>(false)
  const [nextOpen, setNextOpen] = useState<string>('')

  //stores
  const D = useStoreI18n.use.dictionary()
  const [revalidateInterval, setRevalidateInterval] = useState<number>(0)

  //counter
  useEffect(() => {
    const interval = setInterval(
      () => {
        setRevalidateInterval((prev) => prev + 1)
      },
      1000 * 60 * 5
    )
    return () => clearInterval(interval)
  }, [revalidateInterval])

  useEffect(() => {
    if (data) {
      if (data.isToCalculateTheOpening) {
        setCalculateOpening(true)
      } else {
        if (data.isUnknown) {
          setIsOpenToday(undefined)
        } else if (data.isOnRequest) {
          setIsOpenToday('onRequest')
          setBadgeLabel(D['fe.plugin.onRequest'])
        } else if (!data.isOpenToday && data.keyForNextOpeningDay === -1) {
          setIsOpenToday('closed')
          setBadgeLabel(D['fe.plugin.closedToday'])
        } else if (data.isOpenToday) {
          setIsOpenToday('open')
          setBadgeLabel(D['fe.plugin.openToday'])
        } else if (data.keyForNextOpeningDay !== -1) {
          setIsOpenToday('closed')
          setBadgeLabel(D['fe.plugin.closed'])
          handleClosedStatus(
            data,
            D,
            setBadgeLabel,
            setNextOpen,
            setIsOpenToday
          )
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  //handle time calculation
  useEffect(() => {
    if (!calculateOpening) {
      return
    }
    const openingTimes =
      data?.weekOpenings && data?.weekOpenings[0]?.openingTimes
    if (!openingTimes) {
      return
    }
    const { isOpen, openTime, closeTime } = getOpeningTimes(openingTimes)

    if (isOpen) {
      setIsOpenToday('open')
      setBadgeLabel(D['fe.plugin.openings.nowOpened'])
      setNextOpen(
        `${D['fe.plugin.openings.closesAt']} ${closeTime} ${D['fe.plugin.openings.h']}`
      )
    } else {
      if (openTime) {
        setIsOpenToday('closed')
        setBadgeLabel(D['fe.plugin.openings.nowClosed'])
        setNextOpen(
          `${D['fe.plugin.openings.opensAt']} ${openTime} ${D['fe.plugin.openings.h']}`
        )
      } else {
        handleClosedStatus(data, D, setBadgeLabel, setNextOpen, setIsOpenToday)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateOpening, revalidateInterval])

  //handle week openings
  useEffect(() => {
    if (
      !data ||
      !data.weekOpenings?.length ||
      data.isOnRequest ||
      data.isUnknown
    ) {
      return
    }
    setWeekOpenings(
      data.weekOpenings
        .map((day, index) => {
          if (!day?.isoWeekday) {
            return null
          }

          const dayLabel = D[`fe.date.day.${day.isoWeekday}`]
          let timeInfo =
            D[
              day.isOpen
                ? 'fe.plugin.openings.nowOpened'
                : 'fe.plugin.openings.nowClosed'
            ]

          if (day.isOpen && day.openingTimes?.length) {
            timeInfo = day.openingTimes
              .map(
                (time: any) =>
                  `${time.opens} - ${time.closes} ${D['fe.plugin.openings.h']}`
              )
              .join(', ')
          }

          return (
            <span
              key={index?.toString()}
              className="gap-x-xs flex justify-between text-neutral-400"
            >
              <span>{dayLabel}</span>
              <span className="text-right">{timeInfo}</span>
            </span>
          )
        })
        .filter(Boolean) // Remove any null entries
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (!isOpenToday || !data) {
    return null
  }

  return isDropDown ? (
    //Detail view
    <Accordion.Container>
      <Accordion.Item
        value={'sidebarOpeningHours'}
        triggerClassName="p-md!"
        contentClassName="p-md! pt-0!"
        head={
          <div className={cn('flex items-center', className)}>
            <div
              className={cn(
                'text-feedback-green textstyle-md-button',
                (isOpenToday === 'closed' || !isOpenToday) &&
                  'text-feedback-red',
                isOpenToday === 'onRequest' && 'text-neutral-400'
              )}
            >
              {badgeLabel}
            </div>
            {nextOpen && (
              <div className="textstyle-md-regular pl-2xs whitespace-nowrap text-neutral-400">
                - {nextOpen}
              </div>
            )}
          </div>
        }
      >
        {weekOpenings && !data.isOnRequest && weekOpenings}
      </Accordion.Item>
    </Accordion.Container>
  ) : (
    //List views
    <div
      className={cn(
        'textstyle-sm-regular absolute bottom-0 left-0 z-10 flex text-neutral-400',
        framed && 'pt-xs pr-xs bg-neutral-100',
        className
      )}
      {...props}
    >
      <StatusChip
        className={cn(
          'gap-x-xs py-2xs px-sm flex items-center justify-start',
          isOpenToday === 'open' && 'bg-feedback-green text-neutral-100',
          (isOpenToday === 'closed' || !isOpenToday) &&
            'bg-feedback-red text-neutral-100',
          isOpenToday === 'onRequest' && 'bg-neutral-300 text-neutral-800'
        )}
        status={
          isOpenToday === 'open'
            ? 'success'
            : isOpenToday === 'closed'
              ? 'error'
              : undefined
        }
        size={size}
      >
        {D[`fe.plugin.${isOpenToday}`]}
      </StatusChip>
      {nextOpen && (
        <div className="textstyle-sm-regular py-2xs px-md bg-hero-alpha flex items-center whitespace-nowrap text-neutral-100">
          {nextOpen}
        </div>
      )}

      {weekOpenings && isBadgeDropdown && (
        <div className="list p-sm duration-default ease-out-cubic absolute top-8 right-0 grid w-full min-w-auto grid-cols-1 border border-neutral-300 bg-neutral-100 opacity-0 transition-opacity">
          {weekOpenings}
        </div>
      )}
    </div>
  )
}

export default memo(OpeningStatus)
